// The board model shared by the Durable Object and the SPA.
//
// Structure (items, edges, positions) is server-authoritative: every change is an
// `Op`, applied in order by the board's Durable Object and recorded in its op log.
// Card text is not here; it lives in the board's Y.Doc (fragment `card:<id>`).

import { z } from 'zod'
import { type Material } from './material'

export const actorSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('user'), id: z.string() }),
  z.object({ kind: z.literal('session'), id: z.string() }),
])
export type Actor = z.infer<typeof actorSchema>

const id = z.string().min(1).max(64)
const coord = z.number().min(-1e6).max(1e6)
const size = z.number().min(0).max(1e5)

/**
 * What a message points at: one or more things on the board, or a quoted
 * passage inside one card (then `items` has exactly that card). Saying
 * something about a selection anchors it to every selected thing.
 */
export const anchorSchema = z
  .object({
    items: z.array(id).min(1).max(100),
    quote: z.string().min(1).max(2000).optional(),
  })
  .refine((a) => a.quote === undefined || a.items.length === 1, {
    message: 'a quote belongs to exactly one item',
  })
export type Anchor = z.infer<typeof anchorSchema>

export const cardDataSchema = z.object({
  /** Null or absent: the card is as tall as its content. A number: fixed height, content scrolls. */
  fixedHeight: z.number().positive().max(1e5).nullable().optional(),
})
export const messageDataSchema = z.object({
  text: z.string().max(20_000),
  author: actorSchema,
  sessionId: id.nullable(),
  replyTo: id.nullable(),
  anchor: anchorSchema.nullable(),
})
export const fileRefSchema = z.object({
  fileKey: z.string().max(200),
  name: z.string().max(255),
  mime: z.string().max(120),
  size: z.number().int().nonnegative(),
})
export type FileRef = z.infer<typeof fileRefSchema>
export const imageDataSchema = fileRefSchema.extend({
  naturalW: z.number().positive().optional(),
  naturalH: z.number().positive().optional(),
})
export const linkDataSchema = z.object({
  url: z.url().max(4000),
  title: z.string().max(500).optional(),
  description: z.string().max(2000).optional(),
  siteName: z.string().max(200).optional(),
  image: z.string().max(4000).optional(),
  favicon: z.string().max(4000).optional(),
})
export const groupDataSchema = z.object({ title: z.string().max(200) })
/** A conversation placed on the board by a person: shows its title and status, opens it. */
export const sessionNodeDataSchema = z.object({ sessionId: id })
/** A material (a PDF in the board's library) placed on the board: a handle that opens it. */
export const materialDataSchema = z.object({ materialId: id, name: z.string().max(255) })
const frac = z.number().min(0).max(1)
/** A region of a page, as fractions of the page's width and height (top-left origin). */
export const pageRectSchema = z
  .object({ x: frac, y: frac, w: frac, h: frac })
  .refine((r) => r.w > 0 && r.h > 0 && r.x + r.w <= 1.0001 && r.y + r.h <= 1.0001, {
    message: 'the region must lie inside the page',
  })
export type PageRect = z.infer<typeof pageRectSchema>
/**
 * Part of a material's page, cut out and placed on the board. Only the recipe
 * is kept (which page, which region); it is drawn from the original PDF.
 */
export const clipDataSchema = z.object({
  materialId: id,
  page: z.number().int().min(1).max(100_000),
  /** What is shown: the region of the page (the viewport). */
  rect: pageRectSchema,
  /**
   * What the clip points at inside what it shows (words of a sentence, part
   * of a figure); the rest is shown muted. Absent: the whole region is the point.
   */
  focus: z.object({ rects: z.array(pageRectSchema).min(1).max(200) }).optional(),
  /** The material's text it points at (the focus, else the whole region), for reading and search. */
  text: z.string().max(20_000).optional(),
})

const base = {
  id,
  x: coord,
  y: coord,
  w: size,
  h: size,
  z: z.number(),
  createdBy: actorSchema,
  createdAt: z.number(),
  updatedAt: z.number(),
  version: z.number().int().nonnegative(),
  /**
   * The group this item belongs to, if any. Membership is explicit state, changed
   * only by explicit actions (drag in/out, creating a group around things, a tool
   * call). It lives on the member so one item can never be in two groups.
   */
  groupId: id.nullable().optional(),
}

export const itemSchema = z.discriminatedUnion('kind', [
  z.object({ ...base, kind: z.literal('card'), data: cardDataSchema }),
  z.object({ ...base, kind: z.literal('message'), data: messageDataSchema }),
  z.object({ ...base, kind: z.literal('image'), data: imageDataSchema }),
  z.object({ ...base, kind: z.literal('file'), data: fileRefSchema }),
  z.object({ ...base, kind: z.literal('link'), data: linkDataSchema }),
  z.object({ ...base, kind: z.literal('group'), data: groupDataSchema }),
  z.object({ ...base, kind: z.literal('session'), data: sessionNodeDataSchema }),
  z.object({ ...base, kind: z.literal('material'), data: materialDataSchema }),
  z.object({ ...base, kind: z.literal('clip'), data: clipDataSchema }),
])
export type Item = z.infer<typeof itemSchema>
export type ItemKind = Item['kind']
export type ItemOf<K extends ItemKind> = Extract<Item, { kind: K }>

export const edgeSchema = z.object({
  id,
  from: id,
  to: id,
  label: z.string().max(200),
  createdBy: actorSchema,
  createdAt: z.number(),
  updatedAt: z.number(),
  version: z.number().int().nonnegative(),
})
export type Edge = z.infer<typeof edgeSchema>

const geometryPatch = z
  .object({ x: coord, y: coord, w: size, h: size, z: z.number(), groupId: id.nullable() })
  .partial()

export const opSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('item.create'), item: itemSchema }),
  z.object({
    type: z.literal('item.update'),
    id,
    patch: geometryPatch.extend({ data: z.record(z.string(), z.unknown()).optional() }),
  }),
  z.object({ type: z.literal('item.delete'), id }),
  z.object({ type: z.literal('edge.create'), edge: edgeSchema }),
  z.object({
    type: z.literal('edge.update'),
    id,
    patch: z.object({ label: z.string().max(200) }),
  }),
  z.object({ type: z.literal('edge.delete'), id }),
])
export type Op = z.infer<typeof opSchema>

export type BoardState = {
  items: Map<string, Item>
  edges: Map<string, Edge>
}

export function emptyState(): BoardState {
  return { items: new Map(), edges: new Map() }
}

export function cloneState(s: BoardState): BoardState {
  return { items: new Map(s.items), edges: new Map(s.edges) }
}

export class OpError extends Error {}

/**
 * Apply one op in place. Throws OpError if the op is invalid against the state
 * (unknown id, duplicate id, immutable field). `now` stamps updatedAt.
 */
export function applyOp(s: BoardState, op: Op, now: number): void {
  switch (op.type) {
    case 'item.create': {
      if (s.items.has(op.item.id)) throw new OpError(`item ${op.item.id} exists`)
      checkGroupId(s, op.item, op.item.groupId)
      s.items.set(op.item.id, op.item)
      return
    }
    case 'item.update': {
      const cur = s.items.get(op.id)
      if (!cur) throw new OpError(`no item ${op.id}`)
      const { data, ...geom } = op.patch
      if (geom.groupId !== undefined) checkGroupId(s, cur, geom.groupId)
      let nextData: Item['data'] = cur.data
      if (data) {
        // Only the words of a message can change (who said it, about what and
        // in reply to what never do); the board decides who may, and until when.
        if (cur.kind === 'message') {
          const field = Object.keys(data).find((k) => k !== 'text')
          if (field !== undefined) throw new OpError(`message field ${field} is immutable`)
        }
        nextData = { ...cur.data, ...data }
        const parsed = itemSchema.safeParse({ ...cur, data: nextData })
        if (!parsed.success) throw new OpError(`invalid data for ${cur.kind}`)
      }
      s.items.set(op.id, {
        ...cur,
        ...stripUndefined(geom),
        data: nextData,
        updatedAt: now,
        version: cur.version + 1,
      } as Item)
      return
    }
    case 'item.delete': {
      if (!s.items.delete(op.id)) throw new OpError(`no item ${op.id}`)
      for (const [eid, e] of s.edges) if (e.from === op.id || e.to === op.id) s.edges.delete(eid)
      // Deleting a group releases its members; it never deletes them.
      for (const [iid, i] of s.items) {
        if (i.groupId === op.id)
          s.items.set(iid, { ...i, groupId: null, updatedAt: now, version: i.version + 1 })
      }
      return
    }
    case 'edge.create': {
      if (s.edges.has(op.edge.id)) throw new OpError(`edge ${op.edge.id} exists`)
      if (!s.items.has(op.edge.from) || !s.items.has(op.edge.to)) {
        throw new OpError('edge endpoint missing')
      }
      s.edges.set(op.edge.id, op.edge)
      return
    }
    case 'edge.update': {
      const cur = s.edges.get(op.id)
      if (!cur) throw new OpError(`no edge ${op.id}`)
      s.edges.set(op.id, { ...cur, ...op.patch, updatedAt: now, version: cur.version + 1 })
      return
    }
    case 'edge.delete': {
      if (!s.edges.delete(op.id)) throw new OpError(`no edge ${op.id}`)
      return
    }
  }
}

/** Apply a batch atomically: either every op applies or the state is untouched. */
export function applyOps(s: BoardState, ops: Op[], now: number): BoardState {
  const next = cloneState(s)
  for (const op of ops) applyOp(next, op, now)
  return next
}

/**
 * Ops that undo `ops` when applied after them. Computed against the state
 * *before* `ops` ran. Item deletes also restore the edges they took with them.
 */
export function invertOps(before: BoardState, ops: Op[]): Op[] {
  const s = cloneState(before)
  const inverse: Op[] = []
  for (const op of ops) {
    inverse.unshift(...invertOne(s, op))
    applyOp(s, op, 0)
  }
  return inverse
}

function invertOne(s: BoardState, op: Op): Op[] {
  switch (op.type) {
    case 'item.create':
      return [{ type: 'item.delete', id: op.item.id }]
    case 'item.update': {
      const cur = s.items.get(op.id)
      if (!cur) return []
      const patch: Extract<Op, { type: 'item.update' }>['patch'] = {}
      for (const k of ['x', 'y', 'w', 'h', 'z'] as const) {
        if (op.patch[k] !== undefined) patch[k] = cur[k]
      }
      if (op.patch.groupId !== undefined) patch.groupId = cur.groupId ?? null
      if (op.patch.data) {
        const prev = cur.data as Record<string, unknown>
        patch.data = Object.fromEntries(Object.keys(op.patch.data).map((k) => [k, prev[k]]))
      }
      return [{ type: 'item.update', id: op.id, patch }]
    }
    case 'item.delete': {
      const cur = s.items.get(op.id)
      if (!cur) return []
      const edges = [...s.edges.values()].filter((e) => e.from === op.id || e.to === op.id)
      const members = [...s.items.values()].filter((i) => i.groupId === op.id)
      // Restore the item first, then its edges and members (inverse ops run in order).
      return [
        { type: 'item.create', item: cur },
        ...edges.map((edge): Op => ({ type: 'edge.create', edge })),
        ...members.map((m): Op => ({ type: 'item.update', id: m.id, patch: { groupId: op.id } })),
      ]
    }
    case 'edge.create':
      return [{ type: 'edge.delete', id: op.edge.id }]
    case 'edge.update': {
      const cur = s.edges.get(op.id)
      return cur ? [{ type: 'edge.update', id: op.id, patch: { label: cur.label } }] : []
    }
    case 'edge.delete': {
      const cur = s.edges.get(op.id)
      return cur ? [{ type: 'edge.create', edge: cur }] : []
    }
  }
}

/** Which item or edge an op touches. */
export function opTarget(op: Op): { kind: 'item' | 'edge'; id: string } {
  switch (op.type) {
    case 'item.create':
      return { kind: 'item', id: op.item.id }
    case 'edge.create':
      return { kind: 'edge', id: op.edge.id }
    case 'item.update':
    case 'item.delete':
      return { kind: 'item', id: op.id }
    case 'edge.update':
    case 'edge.delete':
      return { kind: 'edge', id: op.id }
  }
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>
}

function checkGroupId(s: BoardState, item: Item, groupId: string | null | undefined) {
  if (groupId == null) return
  if (item.kind === 'group') throw new OpError('groups do not nest')
  const g = s.items.get(groupId)
  if (g?.kind !== 'group') throw new OpError(`no group ${groupId}`)
}

/** The items that belong to a group. Membership is explicit (`groupId`), never geometric. */
export function groupMembers(s: BoardState, groupId: string): Item[] {
  return [...s.items.values()].filter((i) => i.groupId === groupId)
}

// ---- wire protocol (JSON text frames; Yjs uses binary frames) ----

export const clientMessageSchema = z.discriminatedUnion('t', [
  /** An atomic batch of ops. `cid` lets the sender match the server's echo. */
  z.object({
    t: z.literal('ops'),
    cid: z.string().max(64),
    ops: z.array(opSchema).min(1).max(500),
  }),
  /** Rendered sizes reported by a client. Not an op: not logged, not undoable. */
  z.object({
    t: z.literal('measure'),
    sizes: z.array(z.object({ id, w: size, h: size })).max(500),
  }),
])
export type ClientMessage = z.infer<typeof clientMessageSchema>

export type ServerMessage =
  | { t: 'init'; seq: number; items: Item[]; edges: Edge[] }
  | { t: 'ops'; seq: number; actor: Actor; cid: string | null; ops: Op[] }
  | { t: 'measured'; sizes: { id: string; w: number; h: number }[] }
  | { t: 'reject'; cid: string; reason: string }
  /**
   * When things last changed, on the server's clock: "item:ID", "edge:ID" and
   * "text:ID" (a card's text) -> ms. All of them on connect, then what changed.
   */
  | { t: 'touched'; at: Record<string, number> }
  /** The board's library (MATERIALS.md): all of it on connect and whenever it changes. */
  | { t: 'materials'; materials: Material[] }

/** First byte of a binary frame. */
export const Y_SYNC = 0
